import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import SEO from "../components/SEO";
import api from "../lib/api";
import {
  Sparkle, Calculator, ArrowLeft, Clock, Moon, Sun,
  Crown, Flame, Skull, Heart, Compass, Path as PathIcon,
  HandsPraying, BookOpen, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import { useSavedReportLoader } from "../lib/useSavedReport";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";

// Curated past-life "mood" illustrations — Unsplash hot-link, royalty-free.
// Picked to evoke the dominant archetype, NOT to literally claim that's the life.
const TONE_HERO = {
  "punya-heavy": {
    img: "https://images.unsplash.com/photo-1614957004131-9e8f2a13123d?w=1200&q=80&auto=format&fit=crop",
    title: "A soul of merit",
    tint: "from-amber-200 via-amber-100 to-yellow-50",
  },
  "karmic-debt": {
    img: "https://images.unsplash.com/photo-1518709594023-6ebb9d2f7e7a?w=1200&q=80&auto=format&fit=crop",
    title: "A soul carrying lessons",
    tint: "from-stone-300 via-stone-200 to-amber-50",
  },
  "mixed": {
    img: "https://images.unsplash.com/photo-1564730435906-fe5e10d36e0d?w=1200&q=80&auto=format&fit=crop",
    title: "A soul on the long road",
    tint: "from-amber-100 via-rose-50 to-stone-50",
  },
};

// Per-chapter mini illustrations + icons — chosen by chapter index
const CHAPTER_VISUAL = [
  { icon: Sparkle, tint: "bg-amber-100 text-amber-700" },           // who you were
  { icon: Compass, tint: "bg-emerald-100 text-emerald-700" },       // land & work
  { icon: Moon,    tint: "bg-indigo-100 text-indigo-700" },         // inner climate
  { icon: Heart,   tint: "bg-rose-100 text-rose-700" },             // love, sex, art
  { icon: BookOpen, tint: "bg-violet-100 text-violet-700" },        // dharma & wealth
  { icon: PathIcon, tint: "bg-stone-200 text-stone-800" },           // karmic debt (Saturn)
  { icon: Flame,   tint: "bg-orange-100 text-orange-700" },         // obsession (Rahu)
  { icon: HandsPraying, tint: "bg-teal-100 text-teal-700" },         // unfinished sadhana
  { icon: Skull,   tint: "bg-red-100 text-red-700" },               // courage / violence (Mars)
  { icon: Clock,   tint: "bg-slate-200 text-slate-700" },           // the moment of leaving
  { icon: Sun,     tint: "bg-yellow-100 text-yellow-700" },         // activating now
];

export default function PastLifePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSavedReportLoader({
    expectedTool: "past_life",
    setForm: (bd) => {
      if (bd?.date) setForm((f) => ({ ...f, date: bd.date }));
      if (bd?.time) setForm((f) => ({ ...f, time: bd.time }));
      if (bd?.place) setForm((f) => ({ ...f, place: bd.place }));
      if (bd?.timezone) setForm((f) => ({ ...f, timezone: bd.timezone }));
    },
    setResult,
  });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter date, time, AND place — D60 needs all three precisely.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/past-life/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='pl-result']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read your D60. Please double-check the birth time (D60 shifts every 2 minutes).");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="past-life-page">
      <SEO
        title="Past Life Reading Calculator — Free D60 (Shashtiamsa) Karmic Chart"
        description="Free Past Life Reading calculator using the Shashtiamsa D60 — Parashara's most subtle divisional chart. Decode your soul's previous incarnation: which planet ruled your past life, the karma you carry forward, and unresolved dharma for this birth."
        path="/past-life"
        keywords={[
          "past life reading", "past life calculator", "past life reading free",
          "past life astrology", "karmic chart", "soul age calculator",
          "d60 shashtiamsa", "shashtiamsa chart", "parashara d60",
          "past life karma reading", "previous life astrology", "karmic past life",
          "soul purpose reading", "past life regression online", "reincarnation chart",
          "past life predictor", "free karmic reading", "past life predictions",
          "vedic past life reading", "past life by date of birth",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "SoftwareApplication",
              "name": "AstroKuber Past Life Reading Calculator",
              "applicationCategory": "AstrologyApplication",
              "operatingSystem": "Any (Web)",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
              "description": "Compute your Shashtiamsa (D60) karmic chart to decode your soul's previous incarnation, the planet that ruled your past life, and the unresolved karma you carry forward.",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "2114",
              },
            },
            {
              "@type": "HowTo",
              "name": "How to compute your Past Life Reading",
              "step": [
                { "@type": "HowToStep", "name": "Enter your exact date, time and place of birth" },
                { "@type": "HowToStep", "name": "We compute your Shashtiamsa (D60) using Swiss Ephemeris" },
                { "@type": "HowToStep", "name": "Receive your past-life planetary ruler, karmic theme and unresolved dharma" },
              ],
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://astrokuberchat.com/" },
                { "@type": "ListItem", "position": 2, "name": "All Tools", "item": "https://astrokuberchat.com/tools" },
                { "@type": "ListItem", "position": 3, "name": "Past Life Reading", "item": "https://astrokuberchat.com/past-life" },
              ],
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is a Past Life Reading in Vedic astrology?",
                  "acceptedAnswer": { "@type": "Answer", "text": "A Past Life Reading uses the Shashtiamsa (D60) — Parashara's most subtle divisional chart, dividing each rashi into 60 parts of 0.5° — to decode the karmic patterns the soul carries from its previous incarnation." },
                },
                {
                  "@type": "Question",
                  "name": "Is the Past Life Reading calculator free?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Yes. The AstroKuber Past Life Reading calculator is completely free — no account, no payment. You only need your date, time and place of birth." },
                },
                {
                  "@type": "Question",
                  "name": "How accurate is the D60 past-life chart?",
                  "acceptedAnswer": { "@type": "Answer", "text": "The D60 is extraordinarily sensitive to birth time — every 2 minutes of time changes the chart. Use a birth-certificate-accurate time for the most reliable past-life reading." },
                },
                {
                  "@type": "Question",
                  "name": "Can the past-life reading tell me what I did in my previous life?",
                  "acceptedAnswer": { "@type": "Answer", "text": "It identifies the planetary deity that ruled your soul's previous incarnation, the dominant karmic theme (artha / kama / dharma / moksha), and the unresolved dharma you have come back to complete." },
                },
                {
                  "@type": "Question",
                  "name": "Do I need to know my exact birth time?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Yes — within 2 minutes is ideal. If your birth time is uncertain, you can still get an approximate reading but the D60 deity may shift to a neighbouring planet." },
                },
              ],
            },
          ],
        }}
      />
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="pl-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Moon size={14} weight="duotone" /> Free preview · Past Life Reading
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Past Life Reading · <span className="italic text-gold">your soul's previous chapter</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          We compute your <b className="text-[#3F2D1B]">D60 (Shashtiamsa)</b> — the most subtle of the 16
          divisional charts in classical Vedic astrology, used in BPHS Ch.6 specifically to read past-life
          karma. We read its <b className="text-[#3F2D1B]">deity-segment names</b> (Ghora, Deva, Kubera, Sarpa,
          Maheshwara, Yaksha…) for the lagna and key planets, and compose an 11-chapter soul-biography of
          who you WERE, what you did, the moment you left, and what karma is activating now.
        </p>

        {/* D60 warning */}
        <div className="mt-6 rounded-2xl bg-amber-50 border-2 border-amber-300 p-4 sm:p-5 flex items-start gap-3" data-testid="pl-d60-warning">
          <Clock size={28} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-950 text-sm sm:text-base">
              D60 needs surgical birth-time accuracy
            </div>
            <p className="text-xs sm:text-sm text-amber-900 mt-1 leading-relaxed">
              The D60 divides every zodiac sign into <b>60 segments of 0°30'</b> — meaning the chart
              changes <b>every ~2 minutes</b> of clock time. A birth recorded as 10:30 vs 10:32 can land
              your soul-tone in a completely different deity. If you only know your birth-time roughly,
              try the closest few times and see which storyline rings truest — the soul recognises itself.
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={analyze} className="mt-6 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pl-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth · be exact</span>
              <input type="time" required value={form.time} onChange={handle("time")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pl-time" />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <input type="text" required value={form.place} onChange={handle("place")} placeholder="Mumbai, India" className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pl-place" />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Timezone</span>
              <select value={form.timezone} onChange={handle("timezone")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pl-tz">
                <option value="Asia/Kolkata">India (IST · UTC+5:30)</option>
                <option value="Asia/Dubai">UAE (UTC+4)</option>
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Europe/London">London (UTC+0/+1)</option>
                <option value="America/New_York">New York (UTC-5/-4)</option>
                <option value="America/Los_Angeles">Los Angeles (UTC-8/-7)</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="pl-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Opening your D60..." : "Read my past life"}
          </button>
        </form>

        {result && (
          <div className="mt-10 space-y-6" data-testid="pl-result">
            <SoulHero story={result.story} />

            {/* Free preview teaser — first chapter visible */}
            <ChapterCard chapter={result.story.chapters[0]} index={0} free />

            {/* Premium gated content — chapters 2-11 + remedies */}
            <PaywallGate
              tool="past_life"
              toolLabel="Past Life Reading · D60 Shashtiamsa"
              birthDetails={{ date: form.date, time: form.time, place: form.place, timezone: form.timezone }}
              report={result}
            >
              <div className="space-y-6">
                {result.story.chapters.slice(1).map((c, i) => (
                  <ChapterCard key={c.title} chapter={c} index={i + 1} />
                ))}

                {/* Remedies */}
                <div className="rounded-2xl bg-[#3F2D1B] text-[#FAF4E8] p-6 sm:p-7 shadow-xl border border-amber-700/40" data-testid="pl-remedies">
                  <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-3">
                    <HandsPraying size={16} weight="duotone" /> Past-life remedies · what your soul is asking for
                  </div>
                  <p className="text-amber-100/85 text-sm leading-relaxed mb-4">
                    These are calibrated to the heaviest karmic signature in your D60. Doing even one of them with
                    regularity will visibly soften the karma it touches.
                  </p>
                  <ul className="space-y-2.5">
                    {result.story.remedies.map((r, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-amber-50">
                        <span className="text-amber-300 flex-shrink-0 mt-0.5">✦</span>
                        <span className="leading-relaxed">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA — go talk to the D60 Past-Life Master */}
                <PastLifeAstrologerCta />
              </div>
            </PaywallGate>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function SoulHero({ story }) {
  const hero = TONE_HERO[story.overall_tone] || TONE_HERO.mixed;
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-xl border border-amber-700/30" data-testid="pl-soul-hero">
      <div
        className="h-52 sm:h-64 bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(63,45,27,0.20), rgba(63,45,27,0.85)), url(${hero.img})` }}
      />
      <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-7 text-[#FAF4E8]">
        <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
          <Sparkle size={14} weight="fill" /> Your D60 reveals
        </div>
        <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
          {hero.title}
        </h2>
        <div className="mt-2 text-amber-100/90 text-sm sm:text-base leading-relaxed max-w-2xl">
          D60 lagna in <b className="text-amber-300">{story.lagna.segment_name}</b> ({story.lagna.segment_quality}) ·
          your soul came in carrying <b className="text-amber-300">{story.overall_tone === "punya-heavy" ? "merit" : story.overall_tone === "karmic-debt" ? "open debt" : "balanced karma"}</b>.
        </div>
      </div>
    </div>
  );
}

function ChapterCard({ chapter, index, free = false }) {
  const visual = CHAPTER_VISUAL[Math.min(index, CHAPTER_VISUAL.length - 1)];
  const Icon = visual.icon;
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7 shadow-sm" data-testid={`pl-chapter-${index}`}>
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center ${visual.tint}`}>
          <Icon size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-serif-display text-xl sm:text-2xl text-[#3F2D1B] leading-tight">
              {chapter.title}
            </h3>
            {free && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold leading-none">
                FREE PREVIEW
              </span>
            )}
          </div>
          <div className="mt-3 text-sm sm:text-base text-[#3F2D1B] leading-relaxed space-y-2 font-light">
            {chapter.body.split(/\n{2,}/).map((para, pi) => (
              <p key={pi}>{renderBold(para)}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PastLifeAstrologerCta() {
  const navigate = useNavigate();
  const [astro, setAstro] = React.useState(null);
  React.useEffect(() => {
    api.get("/astrologers")
      .then(({ data }) => {
        const m = (data || []).find(a => (a.technique || "").includes("D60 Past-Life Reading"));
        setAstro(m || null);
      })
      .catch(() => setAstro(null));
  }, []);

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-7 shadow-xl border border-amber-700/40"
      data-testid="pl-cta-card"
    >
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-3">
        <Sparkle size={14} weight="fill" /> Want it read live by a D60 master?
      </div>
      {astro ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5">
          {astro.avatar && (
            <img
              src={astro.avatar}
              alt={astro.name}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-amber-300/40 flex-shrink-0"
              data-testid="pl-cta-avatar"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight">
              {astro.name}
            </h3>
            <div className="text-amber-300 text-xs sm:text-sm mt-0.5">
              {astro.technique} · {astro.experience_years}+ yrs
            </div>
            <p className="text-amber-100/90 text-xs sm:text-sm mt-2 leading-relaxed line-clamp-3">
              {astro.bio}
            </p>
            <button
              onClick={() => navigate(`/astrologers/${astro.astrologer_id}`)}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-[#1A1410] font-bold text-sm shadow-lg hover:scale-[1.03] transition-transform"
              data-testid="pl-cta-talk-btn"
            >
              <Sparkle size={14} weight="fill" />
              Talk to {astro.name.split(" ")[0]} now · ₹{astro.rate_per_min}/min
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-amber-100/90 flex-1">
            Connecting you with our senior D60 past-life astrologer who can read your segments live...
          </p>
          <button
            onClick={() => navigate("/astrologers")}
            className="px-5 py-2.5 rounded-full bg-amber-400 text-[#1A1410] font-bold text-sm shadow-lg"
            data-testid="pl-cta-talk-btn-fallback"
          >
            Browse astrologers →
          </button>
        </div>
      )}
    </div>
  );
}


function renderBold(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <b key={i} className="text-[#3F2D1B]">{p.slice(2, -2)}</b>;
    }
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) {
      return <i key={i} className="text-gold">{p.slice(1, -1)}</i>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// Eslint: Warning import currently unused inside this file but kept for parity
// with other tool pages (could be used for future precaution callouts).
// eslint-disable-next-line no-unused-vars
const _keep = [Warning, Crown];
